import {type BskyAgent} from '@atproto/api'

import {ClassroomApiError} from './errors'

type QueryParams = Record<
  string,
  string | number | boolean | undefined | string[]
>

async function send<T>(
  agent: BskyAgent,
  url: URL,
  init: RequestInit,
): Promise<T> {
  const accessJwt = agent.session?.accessJwt
  if (!accessJwt) throw new Error('Not authenticated')

  const res = await fetch(url.toString(), {
    ...init,
    headers: {
      Authorization: `Bearer ${accessJwt}`,
      ...(init.body ? {'Content-Type': 'application/json'} : {}),
    },
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string
      message?: string
    }
    throw new ClassroomApiError(
      body.error ?? 'Unknown',
      res.status,
      body.message,
    )
  }
  return res.json() as Promise<T>
}

function xrpcUrl(agent: BskyAgent, method: string) {
  const base = agent.serviceUrl.toString().replace(/\/+$/, '')
  return new URL(`${base}/xrpc/${method}`)
}

export async function classroomQuery<T>(
  agent: BskyAgent,
  method: string,
  params: QueryParams = {},
): Promise<T> {
  const url = xrpcUrl(agent, method)
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    if (Array.isArray(value)) {
      for (const v of value) url.searchParams.append(key, v)
    } else {
      url.searchParams.set(key, String(value))
    }
  }
  return send<T>(agent, url, {})
}

export async function classroomProcedure<T>(
  agent: BskyAgent,
  method: string,
  input: object,
): Promise<T> {
  return send<T>(agent, xrpcUrl(agent, method), {
    method: 'POST',
    body: JSON.stringify(input),
  })
}
